import { z } from "zod"

/**
 * Check if localStorage is available (handles SSR, private browsing)
 */
export function isStorageAvailable(): boolean {
  if (typeof window === "undefined") {
    return false
  }

  try {
    const test = "__storage_test__"
    window.localStorage.setItem(test, test)
    window.localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

/**
 * Save data to localStorage with error handling
 */
export function saveToStorage<T>(key: string, data: T): boolean {
  if (!isStorageAvailable()) {
    return false
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(data))
    return true
  } catch (error) {
    console.error(`Failed to save to localStorage [${key}]:`, error)
    return false
  }
}

/**
 * Load and validate data from localStorage
 */
export function loadFromStorage<T>(key: string, schema: z.ZodSchema<T>): T | null {
  if (!isStorageAvailable()) {
    return null
  }

  try {
    const raw = window.localStorage.getItem(key)

    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw)
    const validated = schema.safeParse(parsed)

    if (!validated.success) {
      console.warn(`Persisted data validation failed [${key}]:`, validated.error)
      return null
    }

    return validated.data
  } catch (error) {
    console.error(`Failed to load from localStorage [${key}]:`, error)
    return null
  }
}

/**
 * Clear data from localStorage
 */
export function clearStorage(key: string): void {
  if (!isStorageAvailable()) {
    return
  }

  try {
    window.localStorage.removeItem(key)
  } catch (error) {
    console.error(`Failed to clear localStorage [${key}]:`, error)
  }
}
